import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  EnvironmentInjector,
  inject,
  Injector,
  input,
  output,
  signal,
  Type,
  viewChild,
  ViewContainerRef,
} from '@angular/core';

const DISMISS_DISTANCE_PX = 96;
const FLING_VELOCITY_PX_PER_MS = 0.4;

interface SheetDrag {
  startY: number;
  lastY: number;
  lastMoveTime: number;
  velocity: number;
}

@Component({
  selector: 'fc-modal-bottom-sheet-container',
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (trapFocus()) {
      <dialog
        #sheetSurface
        class="sheet-surface"
        tabindex="-1"
        (cancel)="onEscape($event)"
        (keydown.escape)="$event.stopPropagation()"
        (click)="onSurfaceClick($event)"
      >
        <ng-container [ngTemplateOutlet]="sheetPanel" />
      </dialog>
    } @else {
      <div
        #sheetSurface
        class="sheet-surface"
        popover="manual"
        role="dialog"
        (click)="onSurfaceClick($event)"
      >
        <ng-container [ngTemplateOutlet]="sheetPanel" />
      </div>
    }

    <ng-template #sheetPanel>
      <div
        class="sheet-panel flex w-full flex-col overflow-hidden rounded-t-2xl bg-white"
        [class.sheet-panel--dragging]="drag() !== null"
        [style.translate]="dragTranslate()"
      >
        <div
          class="flex shrink-0 cursor-grab touch-none justify-center py-8"
          (pointerdown)="startDrag($event)"
          (pointermove)="moveDrag($event)"
          (pointerup)="endDrag()"
          (pointercancel)="endDrag()"
        >
          <div class="h-4 w-64 rounded-full bg-secondary-200"></div>
        </div>
        <ng-container #contentHost />
      </div>
    </ng-template>
  `,
  styles: `
    @layer modal-bottom-sheet {
      :host {
        display: contents;
      }

      .sheet-surface {
        width: 100%;
        max-width: none;
        height: 100%;
        max-height: none;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        overflow: clip;
        outline: none;
        align-items: flex-end;
        transition:
          display 250ms allow-discrete,
          overlay 250ms allow-discrete;
      }

      .sheet-surface:is([open], :popover-open) {
        display: flex;
      }

      .sheet-surface::backdrop {
        background: rgb(0 0 0 / 32%);
        opacity: 0;
        transition:
          opacity 250ms ease,
          display 250ms allow-discrete,
          overlay 250ms allow-discrete;
      }

      .sheet-surface:is([open], :popover-open)::backdrop {
        opacity: 1;

        @starting-style {
          opacity: 0;
        }
      }

      .sheet-panel {
        max-height: 90dvh;
        translate: 0 100dvh;
        transition: translate 250ms cubic-bezier(0.32, 0.72, 0, 1);
      }

      .sheet-surface:is([open], :popover-open) .sheet-panel {
        translate: 0 0;

        @starting-style {
          translate: 0 100dvh;
        }
      }

      .sheet-panel--dragging {
        transition: none;
      }

      @media (prefers-reduced-motion: reduce) {
        .sheet-surface,
        .sheet-surface::backdrop,
        .sheet-panel {
          transition: none;
        }
      }
    }
  `,
})
export class ModalBottomSheetContainerComponent {
  readonly trapFocus = input(true);
  readonly dismissed = output<void>();

  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly sheetSurface =
    viewChild.required<ElementRef<HTMLElement>>('sheetSurface');
  private readonly contentHost = viewChild.required('contentHost', {
    read: ViewContainerRef,
  });

  protected readonly drag = signal<SheetDrag | null>(null);
  private readonly dragOffset = signal(0);
  private isClosing = false;

  protected readonly dragTranslate = computed(() => {
    const offset = this.dragOffset();
    return offset > 0 ? `0 ${offset}px` : null;
  });

  attachContent<TComponent>(
    component: Type<TComponent>,
    injector: Injector,
  ): void {
    this.contentHost().createComponent(component, {
      injector,
      environmentInjector: injector.get(EnvironmentInjector),
    });
  }

  open(): void {
    const surface = this.sheetSurface().nativeElement;
    if (surface instanceof HTMLDialogElement) {
      surface.showModal();
      surface.focus();
      return;
    }
    surface.showPopover();
    this.document.addEventListener('keydown', this.closeOnEscape, {
      capture: true,
    });
    this.destroyRef.onDestroy(() =>
      this.document.removeEventListener('keydown', this.closeOnEscape, {
        capture: true,
      }),
    );
  }

  close(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    this.drag.set(null);
    this.dragOffset.set(0);

    const surface = this.sheetSurface().nativeElement;
    if (surface instanceof HTMLDialogElement) {
      surface.close();
    } else {
      surface.hidePopover();
    }
    Promise.allSettled(
      surface
        .getAnimations({ subtree: true })
        .map((animation) => animation.finished),
    ).then(() => this.dismissed.emit());
  }

  private readonly closeOnEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.isClosing) return;
    event.stopPropagation();
    this.close();
  };

  protected onEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }

  protected onSurfaceClick(event: MouseEvent): void {
    if (event.target === this.sheetSurface().nativeElement) {
      this.close();
    }
  }

  protected startDrag(event: PointerEvent): void {
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.drag.set({
      startY: event.clientY,
      lastY: event.clientY,
      lastMoveTime: event.timeStamp,
      velocity: 0,
    });
  }

  protected moveDrag(event: PointerEvent): void {
    const drag = this.drag();
    if (!drag) return;

    const elapsed = event.timeStamp - drag.lastMoveTime;
    const instantVelocity = elapsed
      ? (event.clientY - drag.lastY) / elapsed
      : drag.velocity;
    this.drag.set({
      ...drag,
      lastY: event.clientY,
      lastMoveTime: event.timeStamp,
      velocity: drag.velocity * 0.6 + instantVelocity * 0.4,
    });
    this.dragOffset.set(Math.max(0, event.clientY - drag.startY));
  }

  protected endDrag(): void {
    const drag = this.drag();
    if (!drag) return;

    const shouldDismiss =
      this.dragOffset() > DISMISS_DISTANCE_PX ||
      drag.velocity > FLING_VELOCITY_PX_PER_MS;
    this.drag.set(null);
    this.dragOffset.set(0);
    if (shouldDismiss) this.close();
  }
}
